import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MediaAsset } from '@inboundhost/shared';
import { api, apiUrl, getToken } from '../lib/api';

export function useMediaList(siteId: string | undefined) {
  return useQuery({
    queryKey: ['media', siteId],
    queryFn: () => api<{ media: MediaAsset[] }>(`/sites/${siteId}/media`),
    enabled: !!siteId,
  });
}

export function useUploadMedia(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const token = getToken();
      const res = await fetch(apiUrl(`/api/sites/${siteId}/media`), {
        method: 'POST',
        headers: token ? { authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const body = await res.text();
        let detail = body.trim();

        if (detail) {
          try {
            const parsed = JSON.parse(detail) as { message?: string; error?: string };
            detail = parsed.message ?? parsed.error ?? detail;
          } catch {
            // Keep the raw text body when it's not JSON.
          }
        } else {
          detail = res.statusText;
        }

        throw new Error(`Upload failed (${res.status}): ${detail || 'Unknown error'}`);
      }
      return (await res.json()) as { media: MediaAsset };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media', siteId] }),
  });
}

export function useDeleteMedia(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) => api<{ ok: true }>(`/sites/${siteId}/media/${mediaId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media', siteId] }),
  });
}
